import { createContext, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { RESOURCES } from './ResourceGrid';
import { TITLE_FONT, PX_FONT } from './theme';
import { GiltCover } from './GiltCover';

/*
 * Pixel-skin section view: each Home Base area is its own book, drawn with Pixel Vault's Book UI sprites
 * (public/assets/bookui). The cover swings open, and each bookmark on the fore-edge is one entry in the
 * book, marked with its icon; picking one turns to that entry's spread.
 */

const K = 3; // sprite pixels → screen pixels (whole numbers keep the pixels crisp)
const BOOK_W = 349, BOOK_H = 208;
const ART = '/assets/bookui';
const FLIP = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 1].map(n => `page${n}`); // one full page turn, back to the open spread
const FRAMES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => `page${n}`).concat(Array.from({ length: 17 }, (_, n) => `open${n}`));
const COVER = Array.from({ length: 17 }, (_, n) => `open${n}`); // closed → open, drawn to match the pack (perspective, light, board edge)
const CLOSED_SHIFT = -67; // sprite px: slides the closed book (right half only) to the centre
export const INK = '#5B3A2A';
export const INK_SOFT = '#8A6248';
export const SEAL = '#8E2F1F';

export const px = (n: number) => n * K;
export const sprite = (src: string, w: number, h: number): CSSProperties => ({
  width: px(w), height: px(h), backgroundImage: `url(${ART}/${src})`, backgroundSize: '100% 100%', imageRendering: 'pixelated',
});

/* Leather for the open book, matching its tome on the study desk. The pack draws one red binding; its four
   leather shades (base, shade, edge, darkest) are swapped for the binding's own, pixel for pixel. */
export const Binding = createContext<string>('red');
const PACK_LEATHER = [[112, 55, 55], [96, 44, 44], [52, 28, 39], [27, 13, 19]];
/* Leathers for the open books: the desk tomes' leathers as the study's candlelight warms them, so a
   book lifted off the desk keeps its colour. [base, shade, edge, darkest] replace the pack's four reds. */
export const LEATHERS: Record<string, number[][]> = {
  red: [[126, 36, 12], [100, 28, 8], [58, 16, 6], [30, 8, 3]], // a big flat cover reads brighter than a spine, so a shade deeper
  blue: [[64, 69, 84], [41, 44, 57], [23, 24, 31], [12, 12, 16]],
  green: [[91, 96, 28], [58, 61, 15], [32, 34, 9], [16, 17, 4]],
  purple: [[101, 44, 53], [68, 29, 38], [37, 16, 21], [19, 8, 11]],
  brown: [[146, 74, 6], [89, 50, 10], [49, 27, 6], [25, 14, 3]],
  grey: [[100, 97, 70], [62, 56, 40], [34, 31, 22], [17, 16, 11]],
};
// Gilt with age on it: duller than new leaf
const GILD: [number[], number[]][] = [[[173, 98, 59], [184, 138, 58]], [[136, 75, 43], [132, 92, 38]], [[173, 119, 87], [208, 172, 102]]];
const GILD_LIT = [222, 190, 112];
const tinted = new Map<string, Promise<string>>();
/** Where the cover face spans (sprite px, at mid-height) in each frame it shows: the gilt follows it as it swings */
export const FACE = new Map<string, [number, number]>();
function tint(frame: string, binding: string): Promise<string> {
  const src = `${ART}/${frame}.png`, to = LEATHERS[binding] ?? null;
  const key = `${binding}/${frame}`;
  if (!tinted.has(key)) tinted.set(key, new Promise(res => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas'); c.width = img.width; c.height = img.height;
      const x = c.getContext('2d')!; x.drawImage(img, 0, 0);
      const im = x.getImageData(0, 0, c.width, c.height), d = im.data;
      // The turning side of the book has thickness: while it swings (open8–open15) its outer and bottom edges get
      // the page block and board the resting side has, so it doesn't read as a flat sheet
      const sn = Number(frame.replace('open', ''));
      if (frame.startsWith('open') && sn >= 8 && sn <= 15) {
        const W = c.width, H = c.height, SPINE = 172, op = (X: number, Y: number) => X >= 0 && Y >= 0 && X < W && Y < H && d[(Y * W + X) * 4 + 3] > 0;
        const PAGE = [[192, 148, 115], [231, 213, 179]], BOARD = [112, 55, 55], BOARD_LO = [96, 44, 44];
        const set = (X: number, Y: number, col: number[]) => { if (X < 0 || X >= W) return; const i = (Y * W + X) * 4; d[i] = col[0]; d[i + 1] = col[1]; d[i + 2] = col[2]; d[i + 3] = 255; };
        const cream = (X: number, Y: number) => { const i = (Y * W + X) * 4; return d[i + 3] > 0 && d[i] > 135 && d[i + 1] > 115 && d[i + 2] > 90; }; // page, lit or shaded
        // Where the turning sheet's outer edge is a bare one-pixel outline against the page, it becomes the page
        // block and the board behind it: two page edges, board, a dark rim
        for (let Y = 0; Y < H; Y++) {
          let x0 = -1; for (let X = 0; X < SPINE; X++) if (op(X, Y)) { x0 = X; break; }
          if (x0 < 0 || ![1, 2, 3, 4].some(k => cream(x0 + k, Y))) continue;
          set(x0, Y, PAGE[0]); set(x0 - 1, Y, PAGE[1]); set(x0 - 2, Y, BOARD); set(x0 - 3, Y, BOARD_LO); set(x0 - 4, Y, [27, 13, 19]);
        }
      }
      // The cover's printed emblem and title plaque are lifted off in every frame the cover shows (closed and
      // swinging): within each row of the cover face, anything that isn't leather between the side rules
      // becomes the leather beside it. Each book stamps its own mark in gilt instead.
      const n = Number(frame.replace('open', ''));
      if (frame.startsWith('open') && n <= 8) {
        const W = c.width, leather = (i: number) => d[i + 3] > 0 && d[i] >= 40 && d[i] <= 150 && d[i] > d[i + 1] * 1.6 && Math.abs(d[i + 1] - d[i + 2]) <= 6;
        for (let y = 86; y <= 160; y++) {
          // The face runs from its first leather pixel to the last one before the pages (cream) or the edge
          let lo = -1, hi = -1;
          for (let X = 150; X <= 335; X++) {
            const i = (y * W + X) * 4;
            if (lo < 0) { if (leather(i)) lo = hi = X; continue; }
            if (!d[i + 3] || (d[i] > 185 && d[i + 1] > 160)) break;
            if (leather(i)) hi = X;
          }
          if (lo < 0 || hi - lo < 6) continue;
          const m = Math.max(2, Math.round((hi - lo) * 0.13));
          // The row's usual leather, to tell the printed emblem's own shading from the face
          const tones: number[] = [];
          for (let X = lo + m; X <= hi - m; X++) { const i = (y * W + X) * 4; if (leather(i)) tones.push(d[i] + d[i + 1] + d[i + 2]); }
          tones.sort((a, b) => a - b);
          const usual = tones[Math.floor(tones.length / 2)] ?? 0;
          let last = (y * W + lo) * 4;
          for (let X = lo; X <= hi - m; X++) {
            const i = (y * W + X) * 4, sum = d[i] + d[i + 1] + d[i + 2];
            if (leather(i) && Math.abs(sum - usual) <= usual * 0.1) { last = i; continue; }
            if (X >= lo + m) { d[i] = d[last]; d[i + 1] = d[last + 1]; d[i + 2] = d[last + 2]; d[i + 3] = 255; }
          }
          // Flat covers: the plaque's shadow is a darker leather, so the middle is laid plain outright
          if (n <= 2) for (let X = 200; X <= 296; X++) { const i = (y * W + X) * 4, f = (70 * W + 240) * 4; d[i] = d[f]; d[i + 1] = d[f + 1]; d[i + 2] = d[f + 2]; d[i + 3] = 255; } // from a plain patch above
          if (y === 120) FACE.set(frame, [lo, hi]);
        }
      }
      // The spine strip beside the cover is drawn rounded, not flat: across its width the leather rolls from shade
      // to a lit crown and back; on the closed book it carries raised bands and gilt rules like the desk tomes
      if (frame.startsWith('open') && sn <= 8) {
        const W = c.width, H = c.height, SP = [52, 28, 39];
        const isSp = (X: number, Y: number) => { const i = (Y * W + X) * 4; return d[i + 3] > 0 && d[i] === SP[0] && d[i + 1] === SP[1] && d[i + 2] === SP[2]; };
        const paint = (X: number, Y: number, col: number[]) => { const i = (Y * W + X) * 4; d[i] = col[0]; d[i + 1] = col[1]; d[i + 2] = col[2]; };
        const ROLL = [[27, 13, 19], [52, 28, 39], [96, 44, 44], [112, 55, 55], [112, 55, 55], [96, 44, 44], [52, 28, 39], [52, 28, 39]];
        for (let Y = 0; Y < H; Y++) {
          let a = -1, b = -1;
          for (let X = 120; X < 200; X++) { if (isSp(X, Y)) { if (a < 0) a = X; b = X; } else if (a >= 0) break; }
          if (a < 0 || b - a < 3) continue;
          const w = b - a + 1;
          for (let X = a; X <= b; X++) paint(X, Y, ROLL[Math.min(ROLL.length - 1, Math.floor(((X - a) / w) * ROLL.length))]);
        }
        if (sn <= 2) {
          // Raised bands: a dark seam, a lit ridge, its shaded side, a dark seam
          for (const y0 of [62, 98, 134, 166]) for (let X = 178; X <= 184; X++) {
            paint(X, y0, [27, 13, 19]); paint(X, y0 + 1, [112, 55, 55]); paint(X, y0 + 2, [96, 44, 44]); paint(X, y0 + 3, [27, 13, 19]);
          }
          // Gilt rules just inside the head and tail
          for (const y0 of [48, 50, 176, 178]) for (let X = 179; X <= 183; X++) paint(X, y0, [173, 98, 59]);
        }
      }
      // While the cover swings (open3–open8) it turns away from the light and stands off the pages: its board edge
      // shows its thickness and it casts a shadow across the page block beside it
      if (frame.startsWith('open') && sn >= 3 && sn <= 8) {
        const W = c.width, H = c.height;
        const hide = (i: number) => d[i + 3] > 0 && d[i] >= 25 && d[i] <= 150 && d[i] > d[i + 1] * 1.5;
        const page = (i: number) => d[i + 3] > 0 && d[i] > 135 && d[i + 1] > 115 && d[i + 2] > 90;
        const edgeW = 3, reach = 8 + (8 - sn) * 4;                 // shadow reaches further while the cover is low
        for (let Y = 0; Y < H; Y++) {
          let hi = -1;
          for (let X = 140; X < W; X++) { const i = (Y * W + X) * 4; if (hide(i)) hi = X; else if (hi >= 0 && page(i)) break; }
          if (hi < 0) continue;
          for (let k = 1; k <= edgeW + reach; k++) {
            const X = hi + k; if (X >= W) break;
            const i = (Y * W + X) * 4; if (!page(i)) continue;
            if (k <= edgeW) { d[i] = 70; d[i + 1] = 32; d[i + 2] = 32; }         // the board's edge, in leather shade
            else { const f = 0.62 + 0.38 * ((k - edgeW) / reach); d[i] *= f; d[i + 1] *= f; d[i + 2] *= f; }
          }
        }
      }
      // The binding's copper tooling (border rules, corner studs, edge trim) is gilded
      // and stands proud of the leather: lit where leather lies above it, a shadow lip on the leather below and right
      const lip: number[] = [], Wd = c.width * 4;
      const isHide = (j: number) => j >= 0 && j < d.length && d[j + 3] > 0 && d[j] >= 25 && d[j] <= 150 && d[j] > d[j + 1] * 1.5;
      for (let i = 0; i < d.length; i += 4) {
        if (!d[i + 3]) continue;
        const g = GILD.findIndex(([from]) => from[0] === d[i] && from[1] === d[i + 1] && from[2] === d[i + 2]);
        // Swinging frames shade the copper into many in-between tones: any copper takes gold at its brightness
        const copper = g < 0 && d[i] >= 70 && d[i] <= 215 && d[i + 2] < 85 && d[i] > d[i + 1] * 1.3 && d[i + 1] > d[i + 2] * 1.2; // not the pages' tans
        if (g < 0 && !copper) continue;
        const k = copper ? (d[i] + d[i + 1] + d[i + 2]) / (173 + 98 + 59) : 1;
        const base = isHide(i - Wd) || isHide(i - 4) ? GILD_LIT : g >= 0 ? GILD[g][1] : GILD[0][1];
        d[i] = Math.min(255, base[0] * k); d[i + 1] = Math.min(255, base[1] * k); d[i + 2] = Math.min(255, base[2] * k);
        for (const j of [i + Wd, i + 4, i + Wd + 4]) if (isHide(j)) lip.push(j);
      }
      // Depth on the cover while it shows: lit from the upper left, falling off toward the lower right, a
      // raised lip where the board meets its edge, and a faint grain in the leather
      const fn = Number(frame.replace('open', '')), shaded = frame.startsWith('open') && fn <= 8;
      const W = c.width, orig = new Uint8ClampedArray(d);
      const isRed = (j: number) => orig[j + 3] > 0 && orig[j] >= 25 && orig[j] <= 150 && orig[j] > orig[j + 1] * 1.5;
      const light = (i: number) => {
        if (!shaded) return 1;
        const p = i / 4, X = p % W, Y = Math.floor(p / W);
        let k = 1.14 - 0.3 * Math.min(1, Math.max(0, ((X - 180) / 130) * 0.55 + ((Y - 34) / 165) * 0.45));
        if (fn >= 3) k *= 1 - 0.065 * (fn - 2);                         // turning away from the light
        if (!isRed(i - W * 4) || !isRed(i - 4)) k *= 1.16;          // top and left edges of the board catch the light
        else if (!isRed(i + W * 4) || !isRed(i + 4)) k *= 0.8;      // bottom and right edges in shade
        const h = Math.sin(Math.floor(X / 2) * 12.9898 + Math.floor(Y / 2) * 78.233) * 43758.5453;
        return k * (0.97 + (h - Math.floor(h)) * 0.06);              // grain, in two-pixel flecks
      };
      if (to) for (let i = 0; i < d.length; i += 4) {
        const n = PACK_LEATHER.findIndex(l => l[0] === d[i] && l[1] === d[i + 1] && l[2] === d[i + 2]);
        if (n >= 0) { const k = light(i); d[i] = Math.min(255, to[n][0] * k); d[i + 1] = Math.min(255, to[n][1] * k); d[i + 2] = Math.min(255, to[n][2] * k); continue; }
        // The swinging frames shade the leather into many in-between reds: carry each shade across by its
        // brightness against the pack's base leather
        const r = d[i], g = d[i + 1], b = d[i + 2];
        if (d[i + 3] && r >= 40 && r <= 150 && r > g * 1.6 && Math.abs(g - b) <= 6) {
          const k = ((r + g + b) / (112 + 55 + 55)) * light(i);
          d[i] = Math.min(255, to[0][0] * k); d[i + 1] = Math.min(255, to[0][1] * k); d[i + 2] = Math.min(255, to[0][2] * k);
        }
      }
      for (const j of lip) { d[j] *= 0.6; d[j + 1] *= 0.6; d[j + 2] *= 0.6; }
      x.putImageData(im, 0, 0);
      c.toBlob(b => res(b ? URL.createObjectURL(b) : src));
    };
    img.onerror = () => res(src);
    img.src = src;
  }));
  return tinted.get(key)!;
}
/** Every frame's image, in the binding's leather once recoloured (the pack's own until then) */
const decoded = new Map<string, Promise<Record<string, string>>>();
/** Every frame of a binding, recoloured and decoded, so no frame ever shows in the pack's own red mid-swing */
export function preloadBinding(binding: string) {
  if (!decoded.has(binding)) decoded.set(binding, Promise.all(FRAMES.map(async f => {
    const src = await tint(f, binding);
    const img = new Image(); img.src = src; await img.decode().catch(() => undefined);
    return [f, src] as const;
  })).then(Object.fromEntries));
  return decoded.get(binding)!;
}
function useFrames(binding: string) {
  const [srcs, setSrcs] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    let alive = true;
    setSrcs(null);
    preloadBinding(binding).then(m => { if (alive) setSrcs(m); });
    return () => { alive = false; };
  }, [binding]);
  return srcs;
}

// Bookmark sprites from the pack (four colours), stretched to more with a hue turn
const MARKS = [{ n: 1 }, { n: 2 }, { n: 3 }, { n: 4 }, { n: 1, hue: 40 }, { n: 2, hue: -60 }, { n: 4, hue: 70 }];

/* ── Page furniture, shared by every book ── */

/** Ribbon title from the pack, text set on top */
export function Title({ children }: { children: ReactNode }) {
  return (
    <div style={{ ...sprite('title-ribbon.png', 80, 16), margin: '0 auto', display: 'grid', placeItems: 'center', flex: 'none' }}>
      <span style={{ fontFamily: PX_FONT, fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK, marginTop: -4 }}>{children}</span>
    </div>
  );
}

export const Rule = () => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: px(2), margin: `${px(1)}px 0 ${px(3)}px`, flex: 'none' }}>
    <span style={{ ...sprite('rule-left.png', 80, 16), width: px(50), height: px(12), backgroundSize: 'auto 100%', backgroundPosition: 'right' }} />
    <span style={{ ...sprite('rule-right.png', 80, 16), width: px(50), height: px(12), backgroundSize: 'auto 100%', backgroundPosition: 'left' }} />
  </div>
);

/** Framed slot from the pack holding a big icon */
export const IconSlot = ({ children, size = 40 }: { children: ReactNode; size?: number }) => (
  <span style={{ ...sprite('slot.png', 22, 22), width: px(size), height: px(size), display: 'grid', placeItems: 'center', color: SEAL, flex: 'none' }}>
    <span style={{ transform: `scale(${(size / 40) * 2.2})`, display: 'grid' }}>{children}</span>
  </span>
);

export const Bullets = ({ items }: { items: ReactNode[] }) => (
  <ul style={{ listStyle: 'none', margin: `${px(4)}px 0 0`, padding: `0 0 0 ${px(6)}px`, alignSelf: 'stretch', display: 'grid', gap: 14 }}>
    {items.map((d, i) => (
      <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontFamily: PX_FONT, fontSize: 13, color: INK }}>
        <span style={{ ...sprite('bullet.png', 20, 20), width: 14, height: 14, flex: 'none' }} />{d}
      </li>
    ))}
  </ul>
);

/** The pack's plank button, as a link or a button */
export function PageButton({ children, href, onClick }: { children: ReactNode; href?: string; onClick?: () => void }) {
  const style: CSSProperties = {
    ...sprite('button.png', 64, 17), marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flex: 'none',
    textDecoration: 'none', fontFamily: PX_FONT, fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK,
    transition: 'transform 0.1s', border: 'none', padding: 0, cursor: 'pointer', backgroundColor: 'transparent',
  };
  return href
    ? <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="sb-btn" style={style}>{children}</a>
    : <button type="button" className="sb-btn" onClick={onClick} style={style}>{children}</button>;
}

/** Re-inks the app's charts and widgets for parchment: every skin token mapped to page ink */
export const PAGE_INK: Record<string, string> = {
  '--pal-text': INK, '--pal-muted': INK_SOFT, '--pal-accent': SEAL, '--pal-win': '#3E6B2E', '--pal-loss': SEAL,
  '--pal-gold': '#C98A2E', '--pal-ink': INK, '--pal-rim': 'rgba(91,58,42,0.55)', '--pal-grid': 'rgba(91,58,42,0.22)',
  '--pal-axis': INK_SOFT, '--pal-area-accent': 'rgba(142,47,31,0.12)', '--pal-area-loss': 'rgba(142,47,31,0.18)',
  '--pal-ui-font': '"Silkscreen", monospace', '--pal-case': 'uppercase',
};

/** Pixel art inked onto the parchment rather than pasted over it */
export const inked: CSSProperties = { filter: 'sepia(0.9) saturate(0.55) contrast(0.95) brightness(1.02)', mixBlendMode: 'multiply', imageRendering: 'pixelated' };

export const Folio = ({ children }: { children: ReactNode }) => (
  <div style={{ marginTop: px(4), fontFamily: PX_FONT, fontSize: 11, color: INK_SOFT, flex: 'none' }}>~ {children} ~</div>
);

/* ── The book ── */

/** Turn to an entry; `again` turns the page even when it's the current one (a fresh sheet) */
export type Go = (key: string, again?: boolean) => void;

export interface BookEntry {
  key: string;
  name: string;
  icon: ReactNode;
  /** Page content; a function gets `go` to turn to another entry */
  left: ReactNode | ((go: Go) => ReactNode);
  right: ReactNode | ((go: Go) => ReactNode);
  /** Shown faded on its bookmark (not ready yet) */
  dim?: boolean;
}

export function Book({ title, entries, start, replay = 0, closed = false }: { title: string; entries: BookEntry[]; start?: string; replay?: number; closed?: boolean }) {
  const [frame, setFrame] = useState('open0');
  const frames = useFrames(useContext(Binding));
  const [pick, setPick] = useState(start ?? entries[0].key);
  const [showing, setShowing] = useState(false); // page content hidden while the pages turn
  const timer = useRef<ReturnType<typeof setInterval>>();

  const play = (seq: string[], ms: number, done?: () => void, mid?: () => void) => {
    clearInterval(timer.current);
    let i = 0;
    timer.current = setInterval(() => {
      setFrame(seq[i]);
      if (i === Math.floor(seq.length / 2)) mid?.();
      i++;
      if (i >= seq.length) { clearInterval(timer.current); done?.(); }
    }, ms);
  };
  // Riffle through the page-turn frames, swapping the spread halfway through
  const turn = (to: string) => { setShowing(false); play(FLIP, 65, () => setShowing(true), () => setPick(to)); };
  // Cover: closed book centred → swings open (or the reverse to close)
  // Waits for the binding's frames, so the cover swings in one leather from first frame to last
  const ready = frames !== null;
  useEffect(() => {
    if (!ready) return;
    if (closed) { setShowing(false); play([...COVER].reverse(), 42); return () => clearInterval(timer.current); }
    setShowing(false); setFrame('open0');
    const t = setTimeout(() => play([...COVER, 'page1'], 42, () => setShowing(true)), 450);
    return () => { clearTimeout(t); clearInterval(timer.current); };
  }, [replay, closed, ready]);
  const go: Go = (key, again) => { if (key !== pick || again) turn(key); };
  const coverIdx = COVER.indexOf(frame);
  const shift = coverIdx < 0 ? 0 : CLOSED_SHIFT * Math.max(0, 1 - coverIdx / (COVER.length - 1));

  const entry = entries.find(e => e.key === pick) ?? entries[0];
  // Content sits inside each page's printed frame (sprite px: left page 64–170, right 186–292, rows 43–174)
  const page: CSSProperties = { position: 'absolute', top: px(43), bottom: px(BOOK_H - 174), width: px(106), display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden',
    transition: 'opacity 0.18s', opacity: showing ? 1 : 0 };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0 20px' }}>
      <style>{`
        @keyframes sb-arrive { from { transform: translateY(16px) scale(0.96); opacity: 0; } to { transform: none; opacity: 1; } }
        .sb-mark { transition: transform 0.18s cubic-bezier(.3,1.6,.5,1); }
        .sb-mark:hover { transform: translateX(${px(3)}px); }
        .sb-btn:hover { filter: brightness(1.08); transform: translateY(-2px); }
        .sb-btn:active { transform: translateY(2px); }
        /* Charts printed on a page: tighter readouts so they fit inside the frame */
        .page-ink span { font-size: 8px !important; letter-spacing: 0.06em !important; }
        .page-ink span span { font-size: 10px !important; }
        .page-ink > div > div:first-child { gap: 4px 10px !important; margin-bottom: 4px !important; }
        @keyframes sb-beam { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
        @keyframes sb-glint { 0%, 100% { opacity: 0; transform: scale(0.6); } 50% { opacity: 1; transform: scale(1); } }
        /* Pixel scrollbar for long pages (quest log, history) */
        .sb-scroll { scrollbar-width: thin; scrollbar-color: ${INK_SOFT} rgba(217,194,140,0.5); }
        .sb-scroll::-webkit-scrollbar { width: 8px; }
        .sb-scroll::-webkit-scrollbar-track { background: rgba(217,194,140,0.5); }
        .sb-scroll::-webkit-scrollbar-thumb { background: ${INK_SOFT}; box-shadow: inset 0 0 0 2px rgba(217,194,140,0.5); }
      `}</style>
      <div key={replay} style={{ position: 'relative', width: px(BOOK_W), height: px(BOOK_H), animation: 'sb-arrive 0.5s cubic-bezier(.25,.9,.3,1)',
        transform: `translateX(${px(Math.round(shift))}px)`, transition: 'transform 0.07s linear' }}>
        {/* Every frame stays mounted and decoded; turning a page only changes which one shows, so nothing flashes */}
        {frames && FRAMES.map(f => (
          <img key={f} src={frames[f]} alt="" draggable={false} decoding="sync" style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', imageRendering: 'pixelated',
            visibility: f === frame ? 'visible' : 'hidden',
          }} />
        ))}

        {/* Bookmarks on the fore-edge: one per entry, marked with its icon */}
        <div style={{ position: 'absolute', top: px(40), left: px(304), display: 'flex', flexDirection: 'column', gap: px(2), opacity: coverIdx < 0 || coverIdx > 12 ? 1 : 0, transition: 'opacity 0.2s' }}>
          {entries.map((e, n) => {
            const on = e.key === entry.key;
            const mark = MARKS[n % MARKS.length];
            return (
              <button key={e.key} type="button" className="sb-mark" title={e.name} onClick={() => !on && turn(e.key)} style={{
                width: px(24), height: px(17), border: 'none', padding: 0, cursor: 'pointer', background: 'none',
                position: 'relative', transform: on ? `translateX(${px(5)}px)` : undefined, opacity: e.dim ? 0.7 : 1,
              }}>
                <span style={{ ...sprite(`mark${mark.n}.png`, 24, 17), position: 'absolute', inset: 0, filter: mark.hue ? `hue-rotate(${mark.hue}deg)` : undefined }} />
                <span style={{ position: 'absolute', left: px(8), top: '50%', transform: 'translateY(-50%) scale(0.5)', transformOrigin: 'left center', color: '#FBEBC8', filter: 'drop-shadow(0 2px 0 rgba(0,0,0,0.45))', display: 'grid' }}>{e.icon}</span>
              </button>
            );
          })}
        </div>

        {/* The cover, tooled in gilt: the area's symbol and the book's name */}
        {coverIdx >= 0 && FACE.has(frame) && <GiltCover title={title} k={K} face={FACE.get(frame)!} />}

        <div key={`l-${entry.key}`} style={{ ...page, left: px(64) }}>{typeof entry.left === 'function' ? entry.left(go) : entry.left}</div>
        <div key={`r-${entry.key}`} style={{ ...page, left: px(186) }}>{typeof entry.right === 'function' ? entry.right(go) : entry.right}</div>
      </div>
    </div>
  );
}

/* ── Resources ── */

const DETAILS: Record<string, string[]> = {
  discord: ['Daily market calls', 'Trade recaps & Q&A', 'Events calendar'],
  whop: ['Membership status', 'Billing & receipts', 'Access roles'],
  course: ['Full VCL / SMOG lessons', 'Replays & worksheets', 'Hosted on Teachable'],
  playbooks: ['VCL, SMOG and TC1 rules', 'Entry checklists', 'Coming soon'],
  marathon: ['Scaling 3 to 25 accounts', 'Cohort rotation', 'Payout planning'],
  youtube: ['Craig Percoco', 'Breakdowns & lives', 'Free to watch'],
  support: ['Account help', 'Billing questions', 'Bug reports'],
};

export function ResourcesBook({ replay, closed }: { replay?: number; closed?: boolean }) {
  const entries: BookEntry[] = RESOURCES.map((r, i) => ({
    key: r.key, name: r.name, icon: r.icon, dim: !r.url,
    left: (
      <>
        <Title>{r.name}</Title>
        <Rule />
        <span style={{ marginTop: px(4) }}><IconSlot>{r.icon}</IconSlot></span>
        <div style={{ fontFamily: TITLE_FONT, fontSize: 28, color: INK, marginTop: px(6) }}>{r.name}</div>
        <div style={{ fontFamily: PX_FONT, fontSize: 12, color: INK_SOFT, marginTop: 6, textAlign: 'center' }}>{r.url ? r.blurb : 'Link coming'}</div>
        <div style={{ marginTop: 'auto' }}><Folio>{i * 2 + 1}</Folio></div>
      </>
    ),
    right: (
      <>
        <Title>Inside</Title>
        <Rule />
        <Bullets items={DETAILS[r.key] ?? []} />
        {r.url && <PageButton href={r.url}>Open <ArrowUpRight size={14} /></PageButton>}
        <Folio>{i * 2 + 2}</Folio>
      </>
    ),
  }));
  return <Book title="Resources" entries={entries} replay={replay} closed={closed} />;
}
